// src/hooks/useInvites.js
// Hook for managing organization invitations

import { useState, useEffect, useCallback } from "react";
import { teamService } from "../api/teamService";

/**
 * Hook for fetching and managing invitations
 *
 * @returns {{
 *   invites: Array,
 *   loading: boolean,
 *   error: string | null,
 *   refetch: () => Promise<void>,
 *   createInvite: (params: Object) => Promise<Object>,
 *   deleteInvite: (id: number) => Promise<void>,
 *   regenerateInvite: (id: number) => Promise<Object>,
 *   creating: boolean,
 *   deleting: number | null,
 *   regenerating: number | null
 * }}
 */
export function useInvites() {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Operation loading states
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [regenerating, setRegenerating] = useState(null);

  const fetchInvites = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await teamService.getInvites();
      // Handle both { invites: [...] } and direct array response
      setInvites(data.invites || data || []);
    } catch (err) {
      console.error("Failed to fetch invites:", err);
      setError(err.message);
      setInvites([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInvites();
  }, [fetchInvites]);

  /**
   * Create a new invitation
   * @param {Object} params - { email?, role?, max_uses?, expires_in_days? }
   * @returns {Promise<Object>} Created invite with code
   */
  const createInvite = useCallback(async (params) => {
    setCreating(true);
    setError(null);

    try {
      const invite = await teamService.createInvite(params);
      // Add to local state
      setInvites((prev) => [invite, ...prev]);
      return invite;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setCreating(false);
    }
  }, []);

  /**
   * Delete/deactivate an invitation
   * @param {number} inviteId
   */
  const deleteInvite = useCallback(async (inviteId) => {
    setDeleting(inviteId);
    setError(null);

    try {
      await teamService.deleteInvite(inviteId);
      // Update local state - mark as inactive
      setInvites((prev) =>
        prev.map((inv) =>
          inv.id === inviteId ? { ...inv, is_active: false } : inv
        )
      );
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setDeleting(null);
    }
  }, []);

  /**
   * Regenerate an invitation code
   * @param {number} inviteId
   * @returns {Promise<Object>} Updated invite with new code
   */
  const regenerateInvite = useCallback(async (inviteId) => {
    setRegenerating(inviteId);
    setError(null);

    try {
      const updatedInvite = await teamService.regenerateInvite(inviteId);
      // Update local state
      setInvites((prev) =>
        prev.map((inv) => (inv.id === inviteId ? updatedInvite : inv))
      );
      return updatedInvite;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setRegenerating(null);
    }
  }, []);

  return {
    invites,
    loading,
    error,
    refetch: fetchInvites,
    createInvite,
    deleteInvite,
    regenerateInvite,
    creating,
    deleting,
    regenerating,
  };
}

/**
 * Get invite status info
 * @param {Object} invite
 * @returns {{ status: string, color: string, canRegenerate: boolean }}
 */
export function getInviteStatus(invite) {
  if (!invite.is_active) {
    return { status: "Inactive", color: "default", canRegenerate: false };
  }

  if (invite.uses >= invite.max_uses) {
    return { status: "Used", color: "success", canRegenerate: true };
  }

  if (invite.expires_at && new Date(invite.expires_at) < new Date()) {
    return { status: "Expired", color: "error", canRegenerate: true };
  }

  return { status: "Active", color: "success", canRegenerate: true };
}

/**
 * Check if an invite is still valid/usable
 * @param {Object} invite
 * @returns {boolean}
 */
export function isInviteValid(invite) {
  if (!invite.is_active) return false;
  if (invite.uses >= invite.max_uses) return false;
  if (invite.expires_at && new Date(invite.expires_at) < new Date()) return false;
  return true;
}
